/**
 * Chat — the guest's conversation with their host.
 *
 * Same division of labour as core/Events.js and core/Planner.js: the API
 * returns data and this turns it into the bubbles and day separators the screen
 * renders. It also owns the one piece of chat state that outlives the screen —
 * whether anything is waiting — because the tab bar has to show the dot from
 * wherever the guest happens to be.
 *
 * The AI concierge answers in the same thread when the house has it on: its
 * answers come back with the guest's own message (send), the guest can hand
 * the thread to the hosts (handover) and ask the concierge again
 * (askConcierge). `effectiveMode()` says who answers right now.
 *
 * Needs a confirmed account — a thread carries a reply back to a person (see
 * App.hasVerifiedAccount). The one exception: a house may let guests without
 * an account ask the concierge (isAnonymousConcierge). That conversation is
 * not stored anywhere but here, and reaching the hosts still needs an account.
 */
import { _lcs, locale } from "../i18n";

export class Chat
{
    /**
     * How often the shell asks whether anything is waiting, while the app is
     * open and the guest is NOT in the chat.
     *
     * A minute is slow for a chat and right for a dot: the dot only says "there
     * is an answer", a host replies in minutes rather than seconds, and this
     * runs for every guest in the house all day. The open thread polls far more
     * often — see ChatState.
     */
    static UNREAD_POLL_MS = 60000;


    constructor( app )
    {
        this.app = app;

        // The open thread (null until loaded).
        this.conversation = null;
        this.messages = [];
        this.hostName = "";
        this.loaded = false;
        this.failed = false;

        // What the tab bar's dot is drawn from.
        this.hasUnread = false;

        // The AI concierge (core-api ConciergeService): its public settings
        // (from the thread, else from the house) and who answers this thread.
        this.concierge = null;
        this.mode = null;

        this._pending = null;
        this._timer = null;
    }

    /** Can this guest chat at all — a confirmed account and a resolved house. */
    isAvailable()
    {
        return this.app.hasVerifiedAccount() && !!this.app.accommodation;
    }

    /** The concierge as the house describes it: { enabled, allowAnonymous, name, greeting, starters }. */
    conciergeInfo()
    {
        return this.concierge || this.app.accommodation?.concierge || { "enabled" : false, "allowAnonymous" : false };
    }

    /**
     * A guest without a confirmed account, at a house that lets them ask the
     * concierge. They get the concierge only — nothing is stored server-side,
     * the conversation lives in this object until the app closes.
     */
    isAnonymousConcierge()
    {
        return !this.isAvailable() && !!this.app.user && !!this.app.accommodation &&
            this.conciergeInfo().allowAnonymous === true;
    }

    /** May the chat screen open at all. */
    canOpen()
    {
        return this.isAvailable() || this.isAnonymousConcierge();
    }

    /** "concierge" — the AI answers; "host" — the guest writes to the hosts. */
    effectiveMode()
    {
        if ( this.isAnonymousConcierge() )
        {
            return "concierge";
        }
        return this.mode || ( this.conciergeInfo().enabled ? "concierge" : "host" );
    }

    _endpoint( suffix = "" )
    {
        return "/chat/guest/" + encodeURIComponent( this.app.accommodation.id ) + suffix;
    }

    /* -------------------------------- Unread --------------------------------- */

    /**
     * Ask whether anything is waiting and update the dot.
     *
     * Never throws and never reports: a guest does not need to be told that a
     * background poll failed, and the dot simply keeps whatever it had.
     *
     * @returns {Promise<boolean>} the new state of the dot.
     */
    async refreshUnread()
    {
        if ( !this.isAvailable() )
        {
            return this._setUnread( false );
        }

        try
        {
            const { result } = await this.app.api.get( this._endpoint( "/unread" ) );
            if ( result && 200 === +result.status && result.data )
            {
                return this._setUnread( result.data.hasUnread === true );
            }
        }
        catch ( e )
        {
            console.error( e );
        }
        return this.hasUnread;
    }

    /**
     * Poll for waiting messages while the app is open.
     *
     * Started once the guest is signed in and stopped on logout. Idempotent, so
     * a second call does not double the polling rate.
     */
    startPolling()
    {
        this.stopPolling();
        if ( !this.isAvailable() )
        {
            return;
        }

        this.refreshUnread();
        this._timer = setInterval( () => this.refreshUnread(), Chat.UNREAD_POLL_MS );
    }

    stopPolling()
    {
        if ( this._timer !== null )
        {
            clearInterval( this._timer );
            this._timer = null;
        }
    }

    _setUnread( hasUnread )
    {
        if ( this.hasUnread !== hasUnread )
        {
            this.hasUnread = hasUnread;
            // The tab bar is chrome, not part of the screen — it is told
            // directly rather than through a re-render.
            this.app.view.setChatUnread( hasUnread );
        }
        return this.hasUnread;
    }

    /* -------------------------------- Thread --------------------------------- */

    /**
     * Load the thread. Concurrent callers share one request; never throws — a
     * failed load leaves the previous messages in place and flags `failed`, so
     * the screen can say so instead of showing an empty conversation.
     */
    async load( force = false )
    {
        if ( !this.isAvailable() )
        {
            this.loaded = true;
            return this.messages;
        }
        if ( !force && this.loaded )
        {
            return this.messages;
        }
        if ( this._pending )
        {
            return await this._pending;
        }

        this._pending = this._fetch().finally( () => { this._pending = null; } );
        return await this._pending;
    }

    async _fetch()
    {
        try
        {
            const { result } = await this.app.api.get( this._endpoint() );
            if ( result && 200 === +result.status && result.data )
            {
                this.conversation = result.data;
                this.messages = Array.isArray( result.data.messages ) ? result.data.messages : [];
                this.hostName = result.data.host?.name || "";
                this.concierge = result.data.concierge || null;
                this.mode = result.data.concierge?.mode || null;
                this.loaded = true;
                this.failed = false;
                this._setUnread( ( +result.data.unreadCount || 0 ) > 0 );
                return this.messages;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        this.failed = true;
        this.loaded = true;
        return this.messages;
    }

    /**
     * Send a message.
     *
     * The caller draws it optimistically; this replaces that draft with the
     * stored message so it carries a real id and the server's timestamp.
     *
     * @returns {Promise<object>} the stored message.
     */
    async send( body )
    {
        const text = String( body || "" ).trim();
        if ( !text )
        {
            throw new Error( _lcs( "Bitte schreib zuerst eine Nachricht." ) );
        }

        if ( this.isAnonymousConcierge() )
        {
            return this._askAnonymous( text );
        }

        try
        {
            // The app's language travels along: the concierge answers in it
            // when the message itself does not say.
            const { result } = await this.app.api.post( this._endpoint(), {
                "body" : text,
                "language" : this.app.l18n.getCurrentLanguage()
            } );
            if ( result && 201 === +result.status && result.data )
            {
                const { replies, mode, ...message } = result.data;
                this.messages.push( message );
                // The concierge's answer (or a status line) comes in the same response.
                ( Array.isArray( replies ) ? replies : [] ).forEach( ( reply ) => this.messages.push( reply ) );
                if ( mode )
                {
                    this.mode = mode;
                }
                this.app.tracker.track( "chat_sent" );
                return message;
            }
            throw new Error( _lcs( "Die Nachricht konnte nicht gesendet werden." ) );
        }
        catch ( e )
        {
            console.error( e );
            throw new Error( Chat.messageForStatus( e ) );
        }
    }

    /**
     * The concierge for a guest without an account: the conversation so far
     * goes along with every question (nothing is stored on the server), and
     * lives here until the app closes.
     */
    async _askAnonymous( text )
    {
        const history = this.messages
            .filter( ( m ) => m.sender === "guest" || m.sender === "concierge" )
            .slice( -10 )
            .map( ( m ) => ( { "sender" : m.sender, "body" : m.body } ) );

        const now = () => new Date().toISOString();
        const question = { "id" : "local-" + Date.now(), "sender" : "guest", "body" : text, "createdAt" : now() };
        this.messages.push( question );

        let data = null;
        try
        {
            const { result } = await this.app.api.post(
                "/concierge/guest/" + encodeURIComponent( this.app.accommodation.id ),
                { history, "question" : text, "language" : this.app.l18n.getCurrentLanguage() } );
            data = result?.data || null;
        }
        catch ( e )
        {
            console.error( e );
        }

        if ( !data || data.unavailable || !data.reply )
        {
            this.messages.push( { "id" : "local-" + Date.now() + "-s", "sender" : "system", "createdAt" : now(),
                "body" : _lcs( "Der Concierge ist gerade nicht erreichbar. Mit einem bestätigten Konto kannst du deinen Gastgebern direkt schreiben." ) } );
        }
        else
        {
            this.messages.push( { "id" : "local-" + Date.now() + "-c", "sender" : "concierge", "createdAt" : now(), "body" : data.reply,
                "meta" : { "handover" : data.handover === true, "unanswered" : data.unanswered === true, "links" : data.links || [] } } );
        }
        this.app.tracker.track( "chat_sent" );
        return question;
    }

    /** "Direkt an Gastgeber schreiben" — the hosts answer this thread from now on. */
    async handover()
    {
        const { result } = await this.app.api.post( this._endpoint( "/handover" ), {} );
        if ( result?.data )
        {
            this.mode = "host";
            if ( result.data.message )
            {
                this.messages.push( result.data.message );
            }
        }
    }

    /** "Concierge fragen" — back to the concierge. */
    async askConcierge()
    {
        const { result } = await this.app.api.post( this._endpoint( "/concierge" ), {} );
        if ( result?.data )
        {
            this.mode = "concierge";
            if ( result.data.message )
            {
                this.messages.push( result.data.message );
            }
        }
    }

    /**
     * Tell the server the guest has read what the host wrote.
     *
     * Sent when the thread is on screen, not when it is fetched — see the API's
     * GuestThreadGetRequest for why those are two different things.
     */
    async markRead()
    {
        if ( !this.isAvailable() )
        {
            return;
        }

        try
        {
            await this.app.api.put( this._endpoint( "/read" ), {} );
            this.messages.forEach( ( message ) =>
            {
                if ( message.sender === "host" && !message.readAt )
                {
                    message.readAt = new Date().toISOString();
                }
            } );
            this._setUnread( false );
        }
        catch ( e )
        {
            // The dot staying lit is the honest outcome of a failed read.
            console.error( e );
        }
    }

    /**
     * Re-read the thread while it is open, and report whether anything arrived.
     *
     * Used by the polling on the chat screen: re-rendering a conversation the
     * guest may be scrolled into on every tick would fight them for the
     * scroll position, so the screen only repaints when this says something is
     * actually new.
     *
     * @returns {Promise<boolean>}
     */
    async refreshThread()
    {
        const before = this.messages.length;
        const lastId = before > 0 ? this.messages[ before - 1 ].id : null;

        await this.load( true );

        const after = this.messages.length;
        return after !== before ||
            ( after > 0 && this.messages[ after - 1 ].id !== lastId );
    }

    /* ------------------------------ Presentation ----------------------------- */

    /**
     * The thread as the screen renders it: bubbles, with a date separator
     * wherever the conversation crosses into a new day.
     */
    getLines()
    {
        const lines = [];
        let lastDay = null;

        this.messages.forEach( ( message ) =>
        {
            const sentAt = new Date( message.createdAt );
            const day = Chat.dayKey( sentAt );

            if ( day !== lastDay )
            {
                lines.push( { "type" : "day", "text" : Chat.dayLabel( sentAt ) } );
                lastDay = day;
            }

            // The concierge's status lines ("an Gastgeber übergeben"). The
            // server writes them in German for the host; the guest reads them
            // in the app's language, by what happened.
            if ( message.sender === "system" )
            {
                lines.push( { "type" : "sys", "id" : message.id, "text" : this._statusText( message ) } );
                return;
            }

            const isConcierge = message.sender === "concierge";
            lines.push( {
                "type" : "message",
                "id" : message.id,
                // "me" is the guest; the host's messages sit on the other side,
                // and so do the concierge's, marked as AI.
                "from" : message.sender === "guest" ? "me" : ( isConcierge ? "concierge" : "them" ),
                "text" : message.body,
                "time" : Chat.timeLabel( sentAt ),
                "links" : isConcierge && Array.isArray( message.meta?.links ) ? message.meta.links : [],
                "offersHandover" : isConcierge && message.meta?.handover === true,
                // Only the guest's own messages show a read marker — a guest
                // has no use for "the host has read your host's message".
                "isRead" : message.sender === "guest" && !!message.readAt
            } );
        } );

        // The handover offer is a question to answer now: only under the last
        // message, only while the concierge still answers this thread.
        const last = lines[ lines.length - 1 ];
        lines.forEach( ( line ) => { line.showOffer = false; } );
        if ( last && last.offersHandover && this.effectiveMode() === "concierge" )
        {
            last.showOffer = true;
        }

        return lines;
    }

    _statusText( message )
    {
        const name = this.hostName || this.app.accommodation?.name || "";
        switch ( message.meta?.event )
        {
            case "handover":
                return _lcs( "Du schreibst jetzt direkt mit {name}. Die Antwort kommt hier.", { name } );
            case "concierge":
                return _lcs( "Der Concierge antwortet wieder. Deine Gastgeber lesen weiter mit." );
            case "concierge-unavailable":
                return _lcs( "Der Concierge ist gerade nicht verfügbar. Deine Nachricht ist bei {name} angekommen — die Antwort kommt hier.", { name } );
            default:
                return message.body;
        }
    }

    /** Local calendar day, so a message at 23:30 is not filed under tomorrow. */
    static dayKey( date )
    {
        return [ date.getFullYear(), date.getMonth() + 1, date.getDate() ].join( "-" );
    }

    static dayLabel( date )
    {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate( yesterday.getDate() - 1 );

        if ( Chat.dayKey( date ) === Chat.dayKey( today ) )
        {
            return _lcs( "Heute" );
        }
        if ( Chat.dayKey( date ) === Chat.dayKey( yesterday ) )
        {
            return _lcs( "Gestern" );
        }

        // Within the last week a weekday is easier to place than a date.
        const daysAgo = Math.round( ( today - date ) / 86400000 );
        if ( daysAgo < 7 )
        {
            return date.toLocaleDateString( locale(), { weekday : "long" } );
        }

        return date.toLocaleDateString( locale(), { day : "2-digit", month : "long", year : "numeric" } );
    }

    static timeLabel( date )
    {
        return date.toLocaleTimeString( locale(), { hour : "2-digit", minute : "2-digit" } );
    }

    /** A failed send as something a guest can read. */
    static messageForStatus( error )
    {
        switch ( +( error && error.status ) )
        {
            case 400: return _lcs( "Diese Nachricht ist zu lang." );
            case 401:
            case 403: return _lcs( "Für den Chat brauchst du ein bestätigtes Konto." );
            case 404: return _lcs( "Dieser Chat ist nicht mehr verfügbar." );
            default:  return _lcs( "Die Nachricht konnte nicht gesendet werden." );
        }
    }

    /** Forget everything — on logout, so the next guest starts clean. */
    reset()
    {
        this.stopPolling();
        this.conversation = null;
        this.messages = [];
        this.hostName = "";
        this.loaded = false;
        this.failed = false;
        this._setUnread( false );
    }
}
